// pages/fire/components/tableFires/TableFire.tsx
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { Box, Chip, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import React from "react";
import { HtmlTooltip } from "../../../infra/components/tooltipsCustom/tooltipsCuston";
import { globalFormatCurrencyBRL, globalFormatDates, globalMaskDocument } from "../../../shared/utils";
import { globalColorFireQuotation } from "../../../shared/utils/globalColorFire";
import { globalTranslateFireStatus } from "../../../shared/utils/globalTranslateFire";
import { TokioMarineLogo } from "../icons/TokioMarineLogo";
import { FireQuotationData } from "../Quotation/components/types/fire";

export type FireTab = "dados" | "reporters" | "documents";

const HEADERS = ["Criação", "Seguradora", "Imobiliária", "Inquilino", "Endereço", "Status", "Prêmio", "Ações"] as const;

interface Props {
	rows: FireQuotationData[];
	totalCount: number;
	page: number;
	rowsPerPage: number;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (value: number) => void;
	onOpenFire?: (id: string, tab?: FireTab) => void;
	onOpenMenu: (event: React.MouseEvent<HTMLElement>, row: FireQuotationData) => void;
}

function renderStatusChip(status: string) {
	const label = globalTranslateFireStatus(status);
	const { bg, color } = globalColorFireQuotation(status);
	return (
		<Chip
			size="medium"
			label={label}
			sx={{
				color,
				fontWeight: 500,
				background: bg,
				border: "0.5px solid #f1f1f1",
			}}
		/>
	);
}

export const TableFire: React.FC<Props> = ({ rows, totalCount, page, rowsPerPage, onPageChange, onRowsPerPageChange, onOpenFire, onOpenMenu }) => {
	return (
		<TableContainer
			component={Paper}
			variant="elevation"
			sx={{
				mt: 1,
				width: "96%",
				px: { xs: 1, sm: 3 },
				py: { xs: 2, sm: 3 },
				ml: 1,
				borderRadius: 2,
				overflow: "hidden",
			}}
		>
			<Table size="small" stickyHeader aria-label="Tabela de Eventos de Fogo">
				<TableHead>
					<TableRow sx={{ backgroundColor: "grey.100" }}>
						{HEADERS.map((header, i) => (
							<TableCell key={header} align={i === HEADERS.length - 1 ? "right" : "left"}>
								<Typography variant="button">{header}</Typography>
							</TableCell>
						))}
					</TableRow>
				</TableHead>

				<TableBody>
					{rows.map((row) => (
						<TableRow key={row.id} hover>
							<TableCell>
								<Typography>{globalFormatDates(row.createdAt)}</Typography>
								<Typography variant="body2" color="text.secondary">
									{row.createdAt ? format(new Date(row.createdAt), "HH:mm:ss", { locale: ptBR }) : ""}
								</Typography>
							</TableCell>

							<TableCell>
								{row.insuranceCompany?.name?.toLowerCase().includes("tokio") ? (
									<Box mt={0.5}>
										<TokioMarineLogo />
									</Box>
								) : (
									<Typography>{row.insuranceCompany?.name}</Typography>
								)}
							</TableCell>

							<TableCell>
								<Typography>{row.client?.name}</Typography>
							</TableCell>

							<TableCell>
								<Typography>{row.tenant_name}</Typography>
								<Typography variant="body2" color="text.secondary">
									{globalMaskDocument(row.tenant_document)}
								</Typography>
							</TableCell>

							<TableCell>
								<HtmlTooltip
									title={`${row.address_street}, ${row.address_number}${row.address_complement ? ` - ${row.address_complement}` : ""}`}
									arrow
									placement="top"
								>
									<Typography
										noWrap
										sx={{
											maxWidth: 200,
											cursor: "default",
											display: "inline-block",
										}}
									>
										{`${row.address_street}, ${row.address_number}${row.address_complement ? ` - ${row.address_complement}` : ""}`}
									</Typography>
								</HtmlTooltip>

								<Typography variant="body2" color="text.secondary">
									{row.address_city} - {row.address_state}
								</Typography>
							</TableCell>

							<TableCell>{renderStatusChip(row.status || "pendente")}</TableCell>

							<TableCell align="center">
								<Typography>{globalFormatCurrencyBRL(row.insurance_prize)}</Typography>
								<Typography variant="caption" color="text.secondary">
									({row.installment_number}X)
								</Typography>
							</TableCell>

							<TableCell align="right">
								<IconButton aria-label={`Ações do evento ${row.id}`} onClick={(e) => onOpenMenu(e, row)}>
									<MoreVertIcon />
								</IconButton>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
			<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
				<TablePagination
					component="div"
					count={totalCount}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={(_, newPage) => onPageChange(newPage)}
					onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
					rowsPerPageOptions={[10, 25, 50]}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</Box>
		</TableContainer>
	);
};
